import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/adminService';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Save, X } from 'lucide-react';

const PopularServices = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    key: '',
    icon: 'car-outline',
    modalType: '',
    position: 1,
    active: true
  });

  const queryClient = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['popularServices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('popular_services')
        .select('*')
        .order('position', { ascending: true });

      if (error) throw error;
      return data || [];
    }
  });

  const createMutation = useMutation({
    mutationFn: async (newItem) => {
      const { data, error } = await supabase
        .from('popular_services')
        .insert([newItem])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['popularServices']);
      setShowAddModal(false);
      setFormData({ label: '', key: '', icon: 'car-outline', modalType: '', position: 1, active: true });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from('popular_services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['popularServices']);
      setEditingItem(null);
      setFormData({ label: '', key: '', icon: 'car-outline', modalType: '', position: 1, active: true });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('popular_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['popularServices']);
    }
  });

  const handleAdd = () => {
    if (!formData.label || !formData.key) {
      alert('Label and Key are required');
      return;
    }
    createMutation.mutate({
      ...formData,
      position: items.length + 1
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      label: item.label || '',
      key: item.key || '',
      icon: item.icon || 'car-outline',
      modalType: item.modal_type || '',
      position: item.position || 1,
      active: item.active !== false
    });
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (!formData.label || !formData.key) {
      alert('Label and Key are required');
      return;
    }
    updateMutation.mutate({
      id: editingItem.id,
      updates: {
        label: formData.label,
        key: formData.key,
        icon: formData.icon,
        modal_type: formData.modalType,
        position: formData.position,
        active: formData.active
      }
    });
  };

  const handleToggleActive = (item) => {
    updateMutation.mutate({
      id: item.id,
      updates: { active: !item.active }
    });
  };

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this service?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Service Management</h1>
          <p className="text-gray-600 mt-1">Manage customer-facing service tiles and modal behavior</p>
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setFormData({ label: '', key: '', icon: 'car-outline', modalType: '', position: 1, active: true });
            setShowAddModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <Plus size={20} />
          <span>Add Service</span>
        </button>
      </div>

      {/* Services List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{item.label}</h3>
                <p className="text-sm text-gray-500">Key: {item.key}</p>
              </div>
              <button
                onClick={() => handleToggleActive(item)}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  item.active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {item.active ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Icon:</span>
                <span className="text-sm font-medium">{item.icon || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Modal Type:</span>
                <span className="text-sm font-medium">{item.modal_type || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Position:</span>
                <span className="text-sm font-medium">{item.position || 1}</span>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(item)}
                className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center justify-center space-x-2"
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center space-x-2"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingItem ? 'Edit Service' : 'Add New Service'}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Label *</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Service label"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Key *</label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Unique key"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Icon name (e.g., car-outline)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modal Type</label>
                <input
                  type="text"
                  value={formData.modalType}
                  onChange={(e) => setFormData({ ...formData, modalType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Modal type (optional: periodic, ac, etc.)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                <input
                  type="number"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Active</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingItem ? handleUpdate : handleAdd}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>{editingItem ? 'Update' : 'Add'} Service</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopularServices;

